import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Card,
  CardContent,
  Fade,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";

const drawerWidth = 240;

function Lessons() {
  const [searchTerm, setSearchTerm] = useState("");
  const [lessons, setLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
const [openContentId, setOpenContentId] = useState(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchLessons = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${apiUrl}/Teacher_Lessoon/GetLesson_Teacher`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        });

        if (!response.ok) throw new Error("Failed to fetch lessons");

        const data = await response.json();

        if (data.data === null && data.msg === "Record Not Found") {
          setError("No lessons found");
          setLessons([]);
          setFilteredLessons([]);
          return;
        }

        setLessons(data);
        setFilteredLessons(data);
        setError("");
      } catch (err) {
        setError("Error loading lessons");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLessons();
  }, [apiUrl]);

  // Client-side search
  useEffect(() => {
    const filtered = lessons.filter((lesson) =>
      lesson.lessonName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLessons(filtered);
  }, [searchTerm, lessons]);

  const handleAddLessonClick = () => navigate("/addlesson");

  const handleEditLesson = (lessonId) => navigate(`/editlesson/${lessonId}`);

  const handleDeleteLesson = async (lessonId) => {
    try {
      const response = await fetch(
        `${apiUrl}/Teacher_Lessoon/Id?LessonId=${lessonId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        setLessons((prev) => prev.filter((l) => l.lessonId !== lessonId));
        setFilteredLessons((prev) => prev.filter((l) => l.lessonId !== lessonId));
      } else {
        setError("Failed to delete lesson");
      }
    } catch (err) {
      console.error(err);
      setError("Error deleting lesson");
    }
  };

  const handleOpenDialog = (lessonId) => {
    setLessonToDelete(lessonId);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setLessonToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (lessonToDelete) handleDeleteLesson(lessonToDelete);
    handleDialogClose();
  };

  const getPdfUrl = (pdfPath) => {
    if (!pdfPath) return "#";
    if (pdfPath.startsWith("http")) return pdfPath;
    return `${apiUrl.replace("/api", "")}/${pdfPath}`;
  };

  // ✅ Professional table header style (FINAL – single source)
const headerCellStyle = {
  backgroundColor: "#f9fafb",
  color: "#374151",
  fontWeight: 700,
  fontSize: "0.95rem",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  textAlign: "center",
  py: 2,
  borderBottom: "2px solid #e5e7eb",
};


  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 7, sm: 8 },
        p: { xs: 2, md: 4 },
        background: "linear-gradient(135deg, #f5f5f5, #ffffff)",

        minHeight: "100vh",
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      <Fade in timeout={500}>
        <Stack spacing={4} sx={{ maxWidth: { xs: "100%", lg: 1400 }, mx: "auto" }}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
              bgcolor: "linear-gradient(180deg, #f8fafc, #ffffff)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Title */}
            <Box
  sx={{
    bgcolor: "transparent",          // ✅ clean professional look
    borderRadius: 2,
    py: 1.5,
    px: 0,
    textAlign: "left",               // ✅ move to start
    mb: 3,
  }}
>
  <Typography
    variant="h4"
    sx={{
      color: "#9a3412",               // ✅ dark orange (Adarsh Vidyalay)
      fontWeight: 800,
      fontSize: { xs: "1.7rem", md: "2.2rem" },
      textTransform: "uppercase",     // ✅ CAPITAL LETTERS
      letterSpacing: "1px",
      textShadow: "1px 1px 3px rgba(154,52,18,0.25)",
    }}
  >
    MY LESSONS
  </Typography>
</Box>


              {/* Search + Add Button */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                mb={4}
              >
                <TextField
                  placeholder="Search lessons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 400 },
                    "& .MuiOutlinedInput-root": {
                      height: 50,
                      bgcolor: "white",
                      "& fieldset": { borderColor: "#ddd" },
                     "&:hover fieldset": { borderColor: "#d97706" },
"&.Mui-focused fieldset": {
  borderColor: "#d97706",
  borderWidth: 2,
  boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
},

                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleAddLessonClick}
                  sx={{
                    minWidth: 200,
                    height: 50,
                    
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                   
                    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
boxShadow: "0 6px 16px rgba(217,119,6,0.35)",
"&:hover": {
  background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
  boxShadow: "0 8px 20px rgba(217,119,6,0.45)",
  transform: "translateY(-2px)",


                  
                    },
                  }}
                >
                  Add New Lesson
                </Button>
              </Stack>

              {/* Error / Loading / Empty */}
              {loading ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <CircularProgress sx={{ color: "#d97706" }} size={60} />
                </Box>
              ) : error ? (
                <Typography color="error" align="center" sx={{ py: 8 }}>
                  {error}
                </Typography>
              ) : filteredLessons.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
                  No lessons found
                </Typography>
              ) : (
             /*   <TableContainer
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  }}
                > */
                <TableContainer
  sx={{
    borderRadius: 2,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    overflow: "hidden",
  }}
>

               <Table stickyHeader sx={{ tableLayout: "fixed" }}>

                   <TableHead>
  <TableRow>
    <TableCell sx={headerCellStyle}>Lesson Name</TableCell>
    <TableCell sx={headerCellStyle}>Class</TableCell>
    <TableCell sx={headerCellStyle}>Content</TableCell>
    <TableCell sx={headerCellStyle}>Actions</TableCell>
  </TableRow>
</TableHead>

                    <TableBody>
                      {filteredLessons.map((lesson, index) => (
                        <TableRow
                          key={lesson.lessonId}
                          sx={{
    "&:hover": { backgroundColor: "#f9fafb" },
    transition: "background 0.2s ease",
  }}
                           
                        >
                       <TableCell
  sx={{
    py: 2,
    fontWeight: 500,
    color: "#111827",
    wordBreak: "break-word",
  }}
>

                            {lesson.lessonName}
                          </TableCell>
                        <TableCell
  sx={{
    py: 2,
    textAlign: "center",
    color: "#374151",
    fontWeight: 500,
  }}
>

                            {lesson.className}
                          </TableCell>
<TableCell
  sx={{
    py: 2,
    color: "#4b5563",
    fontSize: "0.95rem",
    lineHeight: 1.6,
    position: "relative",
  }}
>
  {/* SHORT CONTENT */}
  <Box
    sx={{
      maxWidth: "220px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: "pointer",
      color: "#2563eb",
      fontWeight: 500,

      "&:hover + .lesson-popup": {
        display: "block",
      },
    }}
  >
    {lesson.lessonContent}
  </Box>

  {/* 🔵 HOVER POPUP */}
  <Box
    className="lesson-popup"
    sx={{
      display: "none",
      position: "absolute",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      mt: 1,

      backgroundColor: "#2563eb",
      color: "#ffffff",
      padding: "12px 16px",
      borderRadius: "8px",
      fontSize: "0.95rem",
      minWidth: "260px",
      maxWidth: "360px",
      boxShadow: "0 8px 22px rgba(0,0,0,0.25)",
      zIndex: 2000,
      lineHeight: 1.6,

      "&::after": {
        content: '""',
        position: "absolute",
        bottom: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        borderWidth: "6px",
        borderStyle: "solid",
        borderColor: "transparent transparent #2563eb transparent",
      },
    }}
  >
    {lesson.lessonContent}
  </Box>
</TableCell>


                        <TableCell sx={{ py: 2 }}>
  <Stack direction="row" spacing={1} justifyContent="center">

    <Tooltip title="View PDF">
      <IconButton
        onClick={() =>
          window.open(getPdfUrl(lesson.lessionPdfUrl), "_blank")
        }
        sx={{
          border: "1px solid #d1d5db",
          color: "#2563eb",
        }}
      >
        <VisibilityIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Edit">
      <IconButton
        onClick={() => handleEditLesson(lesson.lessonId)}
        sx={{
          border: "1px solid #d1d5db",
          color: "#f59e0b",
        }}
      >
        <EditIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Delete">
      <IconButton
        onClick={() => handleOpenDialog(lesson.lessonId)}
        sx={{
          border: "1px solid #d1d5db",
          color: "#dc2626",
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>

  </Stack>
</TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Fade>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle sx={{ bgcolor: "#f44336", color: "white", fontWeight: 600 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>Are you sure you want to delete this lesson? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Lessons;